<template>
  <div class="modal-parent">
    <div
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <div
        class="modal_topbar"
        style="min-height: 2em"
      >
        <div class="modal_topbar_action">
          <template v-if="loading">
            <loader :size="25" />
          </template>
        </div>
      </div>
      <div class="form-container">
        <div class="form-container center-content">
          <h2 class="mt-0">
            {{ title }}
          </h2>
          <p
            class="mt-0"
            style="min-height: 1em"
          >
            {{ subtitle }}
          </p>
        </div>
        <div class="mt-2">
          <div
            v-if="progressWarning"
            class="warning-icon"
          >
            <span v-html="warningIcon" />
          </div>
          <bio-progress
            :value="progressValue"
            :max="100"
            :message="progressMessage"
            :warning="progressWarning"
          />
        </div>
      </div>
      <div class="modal_footer modal_footer--right">
        <button
          id="close-updating-modal"
          class="btn btn--grey"
          :disabled="loading"
          @click="closeModal"
        >
          close
        </button>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
</template>

<script>
import feather from 'feather-icons'
import Loader from '../loader'
import bioProgress from '../bioProgress.vue'
import deviceQueries from '@/graphql/devices.js'

export default {
  name: 'UpdatingDeviceModal',
  components: {
    loader: Loader,
    bioProgress,
  },
  mixins: [deviceQueries],
  props: ['device'],
  data() {
    return {
      loading: false,
      title: '',
      subtitle: '',
      progressMessage: '',
      progressValue: 0,
      progressWarning: false,
      updatedDevice: null,
      warningIcon: feather.icons['alert-circle'].toSvg({
        width: '3rem',
        height: '3rem',
      }),
    }
  },
  mounted() {
    this.loading = true
    this.getLastUpdatedTime()
  },
  methods: {
    closeModal() {
      if (this.loading) {
        return
      }
      this.$emit('close', this.updatedDevice)
    },

    /*
     * This method gets the latest time the device was updated and then compare with the previous one.
     * If it is newer, it means the device received the changes and acknowledge back
     * Frontend will make that call to API every X amount of time, so we dont make all calls at once, hence the delay inside the loop
     * After 30 seconds (10 calls every 3 seconds) link will assume the API couldn't send the changes to the device and give up
     */
    async getLastUpdatedTime() {
      try {
        const currentDate = new Date()
        await this.delayAndInitialize()

        const success = await this.attemptUpdateCheck(currentDate)
        if (!success) {
          this.setWarningMessage()
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    async delayAndInitialize() {
      const initialWaitTimeInMs = 500
      await this.delay(initialWaitTimeInMs)
      this.setInitialMessage()
    },

    async attemptUpdateCheck(currentDate) {
      /* eslint-disable no-loops/no-loops */
      const numOfAttempts = 10
      const waitTimeInMs = 3000

      for (let i = 0; i < numOfAttempts; i++) {
        await this.delay(waitTimeInMs)

        this.updatedDevice = await this.getDeviceQuery(this.device.serialNumber)
        const newDate = new Date(this.updatedDevice.configUpdatedAt)

        if (newDate > currentDate) {
          this.setSuccessMessage()
          return true
        }
      }
      return false
      /* eslint-enable no-loops/no-loops */
    },

    handleError(error) {
      this.setWarningMessage()
      console.log(error)
    },

    delay(time) {
      return new Promise((resolve) => {
        setTimeout(resolve, time)
      })
    },

    setInitialMessage() {
      this.title = 'Sending your changes ...'
      this.subtitle = `Please wait while we update your settings for ${this.device.name}`
      this.progressWarning = false
      this.progressValue = 50
      this.progressMessage = 'Sending configuration to device'
    },

    setSuccessMessage() {
      this.title = 'Changes have been sent'
      this.subtitle = ''
      this.progressValue = 100
      this.progressMessage = 'Configuration received successfully by device'
      this.loading = false
    },

    setWarningMessage() {
      this.title = 'Unable to send changes'
      this.subtitle = ''
      this.progressValue = 100
      this.progressWarning = true
      this.progressMessage = 'Device unreacheable - please ensure device is online'
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.center-content {
  @include flex-align-items(center);
}
.warning-icon {
  color: $red;
  display: table;
  margin: 0 auto;
}
</style>
